/**
 * Per-device MEMORY BUDGETS for the two unbounded in-memory caches AUD-3 found (F-15 / LB-2).
 *
 * Both used to grow for the whole session with no LRU, no byte cap and no spill:
 *  * `mockupStore.mem` — decrypted bakes at ~13 MB per entry. Browsing 200 templates x 3 views
 *    is ~7.8 GB resident, and the renderer is killed mid-batch ("it crashed again, nothing
 *    saved").
 *  * `batchStore.outputs` — every output's bytes for the whole session, ~1.9 GB after a
 *    1000-item run that already wrote every one of those files to disk.
 *
 * Device-local (localStorage, never synced — same shape as `renderFallback.ts` /
 * `renderBackend.ts`), because the right number depends on the machine, not the workspace.
 * Every read is try/catch'd and falls back to the default: a browser with storage disabled must
 * get a working budget, not a crash.
 */

export const MOCKUP_MEM_BUDGET_KEY = "genz.render.mockupMemBudgetMb";
export const DEFAULT_MOCKUP_MEM_BUDGET_MB = 512;
const MOCKUP_MEM_MIN_MB = 128;
const MOCKUP_MEM_MAX_MB = 4096;

/** Clamp to the band, treating anything unparseable (NaN, empty, a hand-edited string) as the
 *  default — a budget is never allowed to read as 0, which would evict everything instantly. */
function clamp(raw: string | null, dflt: number, min: number, max: number): number {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return dflt;
  return Math.min(max, Math.max(min, Math.round(n)));
}

function read(key: string, dflt: number, min: number, max: number): number {
  try {
    return clamp(localStorage.getItem(key), dflt, min, max);
  } catch {
    return dflt; // private mode / no storage — the default is the safe one
  }
}

function write(key: string, mb: number, min: number, max: number): void {
  try {
    localStorage.setItem(key, String(Math.min(max, Math.max(min, Math.round(mb)))));
  } catch {
    /* private mode / no storage — the in-memory default keeps applying */
  }
}

/** How much DECRYPTED template artwork to keep in RAM (128…4096 MB, default 512). */
export function getMockupMemBudgetMb(): number {
  return read(MOCKUP_MEM_BUDGET_KEY, DEFAULT_MOCKUP_MEM_BUDGET_MB, MOCKUP_MEM_MIN_MB, MOCKUP_MEM_MAX_MB);
}

export function setMockupMemBudgetMb(mb: number): void {
  write(MOCKUP_MEM_BUDGET_KEY, mb, MOCKUP_MEM_MIN_MB, MOCKUP_MEM_MAX_MB);
}

export const OUTPUT_BYTES_BUDGET_KEY = "genz.render.outputBytesBudgetMb";
export const DEFAULT_OUTPUT_BYTES_BUDGET_MB = 512;
const OUTPUT_BYTES_MIN_MB = 64;
const OUTPUT_BYTES_MAX_MB = 4096;

/** How many rendered-output bytes to keep in RAM AFTER the auto-save has written them to disk
 *  (64…4096 MB, default 512). Anything dropped is re-read from the output folder on export. */
export function getOutputBytesBudgetMb(): number {
  return read(
    OUTPUT_BYTES_BUDGET_KEY,
    DEFAULT_OUTPUT_BYTES_BUDGET_MB,
    OUTPUT_BYTES_MIN_MB,
    OUTPUT_BYTES_MAX_MB,
  );
}

export function setOutputBytesBudgetMb(mb: number): void {
  write(OUTPUT_BYTES_BUDGET_KEY, mb, OUTPUT_BYTES_MIN_MB, OUTPUT_BYTES_MAX_MB);
}
